import { Request, Response, NextFunction, RequestHandler } from 'express';
import { Validator, ValidationOptions as BaseValidationOptions } from '@unidev-hub/validator';
import { HttpError } from './error-handler';
import { getConfig } from './config';
import validator from '@unidev-hub/validator';

// Get validation utilities from validator
const { errors } = validator;

/**
 * Generic validator function that can validate different parts of the request
 * @param schema Validator schema
 * @param source Request property to validate ('body', 'query', 'params', 'headers')
 * @param options Validation options
 */
export function validate(
  schema: Validator<any>,
  source: 'body' | 'query' | 'params' | 'headers' = 'body',
  options: BaseValidationOptions = {}
): RequestHandler {
  return async (req: Request, res: Response, next: NextFunction): Promise<void> => {
    try {
      // Get the data to validate
      const data = req[source];

      // Get middleware configuration
      const config = getConfig();

      // Handle empty data case - check if schema requires data
      const schemaDescription = schema.getSchema().describe();
      const isRequired = schemaDescription.flags &&
        'presence' in schemaDescription.flags &&
        schemaDescription.flags.presence === 'required';

      if ((!data || (typeof data === 'object' && Object.keys(data).length === 0)) && isRequired) {
        throw new HttpError(
          `Request ${source} is required`,
          400,
          'VALIDATION_ERROR',
          { details: { [source]: [`Request ${source} is required`] } }
        );
      }

      // Setup validation options by merging config with provided options
      const validationOptions = {
        abortEarly: options.abortEarly ?? config.validation?.abortEarly,
        stripUnknown: options.stripUnknown ?? config.validation?.stripUnknown,
        allowUnknown: options.allowUnknown ?? config.validation?.allowUnknown
      };

      // Validate the data
      const result = schema.validate(data, validationOptions);

      if (result.valid) {
        // Replace request data with validated and transformed data
        if (source !== 'query') {
          // For other sources (body, params, headers), we can replace safely
          req[source] = result.value;
        }

        next();

      } else {
        // Create an HttpError for consistent error handling
        const formattedErrors = errors.validationErrorToResponse(result.error!);

        if (config.validation?.useHttpError) {
          throw new HttpError(
            'Validation failed',
            400,
            'VALIDATION_ERROR',
            { details: formattedErrors.errors }
          );
        } else {
          res.status(400).json({
            success: false,
            message: 'Validation failed',
            errors: formattedErrors.errors
          });
        }
      }
    } catch (error: any) {
      // Get middleware configuration
      const config = getConfig();

      // If using HttpError and it's already an HttpError, pass it through
      if (config.validation?.useHttpError && error instanceof HttpError) {
        next(error);
        return;
      }

      // If it has details property, treat as a ValidationError
      if (error && error.details) {
        const formattedErrors = errors.validationErrorToResponse(error);

        if (config.validation?.useHttpError) {
          next(new HttpError(
            'Validation failed',
            400,
            'VALIDATION_ERROR',
            { details: formattedErrors.errors }
          ));
        } else {
          res.status(400).json({
            success: false,
            message: 'Validation failed',
            errors: formattedErrors.errors
          });
        }
        return;
      }

      // For any other error
      if (config.validation?.useHttpError) {
        next(new HttpError(
          error?.message || 'Validation error',
          400,
          'VALIDATION_ERROR'
        ));
      } else {
        res.status(400).json({
          success: false,
          message: error?.message || 'Validation error'
        });
      }
    }
  };
}

/**
 * Shorthand for validating request body
 * @param schema Validator schema
 * @param options Validation options
 */
export function validateBody(
  schema: Validator<any>,
  options: BaseValidationOptions = {}
): RequestHandler {
  return validate(schema, 'body', options);
}

/**
 * Shorthand for validating query parameters
 * @param schema Validator schema
 * @param options Validation options
 */
export function validateQuery(
  schema: Validator<any>,
  options: BaseValidationOptions = {}
): RequestHandler {
  return validate(schema, 'query', options);
}

/**
 * Shorthand for validating route parameters
 * @param schema Validator schema
 * @param options Validation options
 */
export function validateParams(
  schema: Validator<any>,
  options: BaseValidationOptions = {}
): RequestHandler {
  return validate(schema, 'params', options);
}

/**
 * Shorthand for validating request headers
 * @param schema Validator schema
 * @param options Validation options
 */
export function validateHeaders(
  schema: Validator<any>,
  options: BaseValidationOptions = {}
): RequestHandler {
  return validate(schema, 'headers', options);
}

/**
 * Validates multiple request properties at once
 * @param schemas Object mapping request properties to validator schemas
 * @param options Validation options
 */
export function validateAll(
  schemas: {
    body?: Validator<any>;
    query?: Validator<any>;
    params?: Validator<any>;
    headers?: Validator<any>;
  },
  options: BaseValidationOptions = {}
): RequestHandler {
  return async (req: Request, res: Response, next: NextFunction): Promise<void> => {
    // Process schemas in order: headers, params, query, body
    const sources: Array<keyof typeof schemas> = ['headers', 'params', 'query', 'body'];

    // Create a middleware chain
    const chain = sources.reduce((chain, source) => {
      const schema = schemas[source];
      if (!schema) return chain;

      return (req: Request, res: Response, innerNext: NextFunction) => {
        chain(req, res, (err) => {
          if (err) return innerNext(err);
          validate(schema, source as any, options)(req, res, innerNext);
        });
      };
    }, (req: Request, res: Response, innerNext: NextFunction) => innerNext());

    // Execute the chain
    chain(req, res, next);
  };
}

/**
 * Creates a middleware that validates both query parameters and request body
 * @param querySchema Schema for validating query parameters
 * @param bodySchema Schema for validating request body
 * @param options Optional validation options
 */
export function validateRequestAndQuery(
  querySchema: Validator<any>,
  bodySchema: Validator<any>,
  options: BaseValidationOptions = {}
): RequestHandler {
  return validateAll({
    query: querySchema,
    body: bodySchema
  }, options);
}

// Export validator for convenience
export { v } from '@unidev-hub/validator';
